import { pageTitle } from '@palaccount/config';
import { AGING_BANDS, type AgingBand } from '@palaccount/contracts';
import type { EChartsOption } from 'echarts';
import { Info } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Chart } from '@/components/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableCell } from '@/components/ui/table';
import { useCan } from '@/lib/permissions';
import { useCompanyToday } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useFinancialDashboard } from './api';
import { HomeTabs } from './home-tabs';
import { ChangePill } from './widgets';

/**
 * NFR-14's ramp across the five bands, keyed by the shared band list so the
 * map cannot fall out of step with what is drawn. Green while nothing is late,
 * blue and amber through the middle, red at 61–90 and 90+ — F-14's "the debt
 * worth chasing", and the same threshold `overdueTone` turns red at.
 */
const AGING_COLOURS: Readonly<Record<AgingBand, string>> = {
  current: 'bg-emerald-500',
  d1_30: 'bg-blue-500',
  d31_60: 'bg-amber-500',
  d61_90: 'bg-red-500',
  d90_plus: 'bg-red-700',
};

/**
 * DSH-01 — the fixed home page. Four KPI cards in a fixed order
 * (BR-DSH-01-01), Income vs. Expenses over the half-year (BR-DSH-01-05),
 * A/R aging in FIVE coloured bands summing to the outstanding total
 * (BR-DSH-01-07, widened from four by decision 5), the five newest posted
 * lines (BR-DSH-01-10). No toolbar, no customization, nothing writes
 * (BR-DSH-01-12).
 *
 * BR-DSH-01-09's client half: a user without ledger read is shown the other
 * boards instead of broken figures — the data endpoint refuses regardless.
 */

/**
 * BR-DSH-01-08 — "The view all action must open the full list of journal
 * lines." Where that list IS comes from PART 14 §14.9, ROW 15 of its
 * open-questions table:
 *
 *   | 15 | Where does the **view all** link on the Financial Dashboard go? |
 *   | `BR-DSH-01-08` | Journal Entry (`FIN-02`), filtered to the last 30 days |
 *
 * ── THAT IS A ROW NUMBER, NOT AN IDENTIFIER — DO NOT WRITE IT AS ONE ──────
 *
 * It is tempting to abbreviate "row 15 of §14.9" to an id-shaped token, and
 * an earlier revision of this comment did. It is not one. The `OQ` family in
 * this spec is `OQ-<part>-<nn>`, so the id-shaped abbreviation collides with
 * PART 15's admin questions (`OQ-15-01`…`OQ-15-18`), which have nothing to do
 * with dashboards — and it names an identifier the specification never
 * defines, which `rule-coverage.spec.ts` tests (3) and (5a) correctly refuse
 * as a dangling citation. The row is cited by NUMBER here for that reason.
 *
 * ── READ THAT ROW'S COLUMN HEADING BEFORE TREATING IT AS SETTLED ──────────
 *
 * §14.9 is "Open questions for this part" and its fourth column is headed
 * **"Proposed default"**, not "Answer". Row 15 is therefore still formally
 * OPEN — unlike rows 5, 6, 12 and 16 of the same table, it is not struck
 * through and not marked **Closed.** What licenses building to it anyway is
 * the section's own preamble:
 *
 *   "Every one has a proposed default already written into the sections
 *    above, so no build is blocked."
 *
 * So this is a PROPOSED DEFAULT, built because the spec pre-authorises
 * building to one — and overrulable by the owner without anybody here having
 * been wrong. `RULE-COVERAGE` classes the whole `OQ` family as a decision
 * rather than an obligation, so the row is quoted here as PROVENANCE for the
 * thirty days and is not a citation; the countable rule is `BR-DSH-01-08`.
 *
 * ── THE THIRTY DAYS ARE HALF THE DESTINATION ──────────────────────────────
 *
 * `BR-DSH-01-08`'s sentence names no screen, so a link landing on a bare,
 * unfiltered `FIN-02` satisfies it word for word while dropping what row 15
 * actually specifies. The range travels in the URL and `JournalsListPage`
 * reads it from there — the two halves are asserted together in
 * `test/dsh01-view-all.test.tsx`.
 *
 * ── ONE CLOCK, NOT THIS BROWSER'S ─────────────────────────────────────────
 *
 * The window is measured from `useCompanyToday()` (N-16, EC-19-65: "there is
 * exactly one today across the whole system, read from the company's time
 * zone"). `report-shell.tsx` records what the alternative costs: a preset that
 * read `new Date().toISOString()` gave the UTC date while every document is
 * stamped on the company clock, so in a UTC+3 tenant the window ended
 * yesterday between local midnight and 03:00 — no error, just a smaller
 * number than the truth for three hours a day.
 */
function journalWindow(today: string): string {
  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 30);
  return `/accounting/journals?from=${from.toISOString().slice(0, 10)}&to=${today}`;
}

export function FinancialDashboardPage() {
  const { t } = useTranslation();
  const canLedger = useCan('accounting.view');
  const today = useCompanyToday();
  const { data, isPending, isError } = useFinancialDashboard();

  useEffect(() => {
    document.title = pageTitle(t('shell.pageFinDash'));
  }, [t]);

  const chartOption = useMemo<EChartsOption>(() => {
    const chart = data?.chart ?? [];
    return {
      grid: { left: 8, right: 8, top: 24, bottom: 24, containLabel: true },
      legend: { data: [t('dash.income'), t('dash.expenses')], top: 0 },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: chart.map((c) => c.label) },
      /**
       * BR-DSH-01-06's other half: the caption below names the scale, so the
       * axis has to BE that scale. The series stay in real currency units —
       * the tooltip keeps exact figures — and only the axis labels divide,
       * which is §5 field 8's own rendering ("the bars are read in thousands,
       * so a bar at `250` means `$250,000`").
       */
      yAxis: { type: 'value', axisLabel: { formatter: (value: number) => String(value / 1000) } },
      series: [
        { name: t('dash.income'), type: 'bar', data: chart.map((c) => Number(c.income)), itemStyle: { color: '#2563eb' } },
        { name: t('dash.expenses'), type: 'bar', data: chart.map((c) => Number(c.expenses)), itemStyle: { color: '#9ca3af' } },
      ],
    };
  }, [data?.chart, t]);

  return (
    <div className="flex flex-col gap-4">
      {/* No page heading here: this board IS the Home group's landing page, so
          the shell's topbar already prints "Financial Dashboard" as the h1.
          The three customizable boards do carry their own heading, because the
          topbar cannot name a board it has no menu entry for. */}
      <HomeTabs />

      {/**
       * THE THREE PAGE-LEVEL STATES, AND WHAT IS AND IS NOT CITED HERE.
       *
       * `ERR-DSH-01-01` — opened with no ledger permission. Enforced: the
       * refusal below, with the board tabs left standing so BR-DSH-01-09's
       * "sent to a screen they can read instead" has somewhere to point.
       * DEVIATION, recorded rather than quietly copied: §14 gives the
       * sentence as "You do not have permission to view financial figures.
       * Ask your administrator for access."; this build says "You do not
       * have permission to read the general ledger." The permission this
       * screen actually tests is `accounting.view`, so naming the ledger is
       * the truer sentence — but it is a deviation and not an oversight.
       *
       * `ERR-DSH-01-03` — the connection dropped mid-load. Enforced: the
       * alert below. DEVIATION: §14 gives "Could not load the dashboard.
       * Check your connection and reload the page."; this build uses the
       * product-wide `common.loadFailed` that some twenty list and editor
       * screens share. That is a deliberate convention, not drift — and
       * changing one shared string on behalf of one screen is how §14.10
       * defect 13 (sales-specific wording leaking onto three dashboards)
       * happened in the other direction.
       *
       * NOT cited here: the shimmer between them. `isPending` is a loading
       * state and no ERR row governs it — a widget that shimmers FOREVER is
       * ERR-DSH-02-06/-08 on the customizable boards, which is another
       * wave's file and not this one's defect.
       */}
      {!canLedger ? (
        <p role="alert" className="py-10 text-center text-sm text-muted-foreground">
          {t('dash.noLedgerPermission')}
        </p>
      ) : isPending ? (
        <div className="h-60 animate-pulse rounded-md bg-muted" aria-busy />
      ) : isError || !data ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          {t('common.loadFailed')}
        </p>
      ) : (
        <>
          {/**
           * GEN-115 + GEN-116, decided by the SERVER exactly as on DSH-04:
           * `rateDate` is non-null precisely when a figure on this page was
           * converted, so the condition below IS the rule rather than the
           * browser's guess at it. A single-currency company sees nothing
           * here at all.
           *
           * GEN-117: informational grey with the info icon, never amber and
           * never red — "a statement of fact, not a warning". The
           * ERR-DSH-04-04 notes ride the same band because they are the same
           * fact's other half: what could NOT be converted, and is therefore
           * missing from the figures below.
           */}
          {data.rateDate === null && data.conversionNotes.length === 0 ? null : (
            <div className="flex flex-col gap-1">
              {data.rateDate === null ? null : (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  {t('dash.consolidated', {
                    currency: data.baseCurrency,
                    date: data.rateDate,
                  })}
                </p>
              )}
              {data.conversionNotes.map((note) => (
                <p key={note} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  {note}
                </p>
              ))}
            </div>
          )}

          <div className="grid gap-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
            {(
              [
                { key: 'dash.kRevenue', kpi: data.totalRevenue, downIsGood: false },
                { key: 'dash.kExpenses', kpi: data.totalExpenses, downIsGood: true },
                { key: 'dash.kNetProfit', kpi: data.netProfit, downIsGood: false },
                { key: 'dash.kCashBalance', kpi: data.cashBalance, downIsGood: false },
              ] as const
            ).map((card) => (
              <Card key={card.key}>
                <CardHeader className="pb-1">
                  <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t(card.key)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/**
                   * BR-DSH-01-03 — "KPI values on this screen must be shown as
                   * whole currency amounts with no decimal places. [This
                   * differs from the customizable dashboards, which always
                   * show 2 decimals. **Decide at build time** whether to align
                   * them; proposed default is to align every dashboard on 2
                   * decimals.]"
                   *
                   * THE DECISION THE BRACKET ASKS FOR IS TAKEN: aligned, on 2
                   * decimals, which is `<Amount>` below. Provenance: the
                   * bracket's own proposed default, restated as PART 14 §14.9
                   * question 14 ("Yes — align every dashboard on 2 decimals")
                   * under the preamble "no build is blocked" — the same
                   * pre-authorisation BR-DSH-01-08 and BR-DSH-00-16 were built
                   * on. The question stays formally open, and the DEVIATION is
                   * stated rather than implied: the rule's first sentence
                   * describes the prototype's whole-dollar rendering, and that
                   * sentence is the half the alignment retires. If the owner
                   * overrules, `dsh01-financial-rules.test.tsx` is the pin to
                   * flip — it exists so this screen cannot drift to a third
                   * rendering while the question is open. (§19's report screens
                   * keep whole dollars on their summary CARDS by their own
                   * rules — BR-DSH-05-14 — which is a different sentence about
                   * different screens, not this decision leaking.)
                   */}
                  <p className="text-2xl font-semibold">
                    <Amount value={card.kpi.value} currency={data.baseCurrency} />
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <ChangePill changePct={card.kpi.changePct} downIsGood={card.downIsGood} />
                    {t('dash.vsLastQuarter')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-[18px] lg:grid-cols-[1.6fr_1fr]">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('dash.incomeVsExpenses')}</CardTitle>
                {/**
                 * BR-DSH-01-06 — "The chart caption must name both the period
                 * and the scale, so that a reader knows the bars are in
                 * thousands." Both halves now hold, and they landed TOGETHER,
                 * which the note that stood here demanded: the caption names
                 * the period and the scale with the currency ("… · in
                 * thousands of USD"), and `chartOption` above prints the
                 * y-axis in thousands so the sentence is true of the axis it
                 * describes (§5 field 8's `First half 2026 · thousands USD`).
                 * The currency is interpolated, not literal — a company that
                 * does not report in dollars gets its own code (GEN-107), and
                 * `dsh01-financial-rules.test.tsx` renders the caption in both
                 * languages to prove the ICU hole substitutes rather than
                 * leaking a brace.
                 */}
                <p className="text-xs text-muted-foreground">
                  {t('dash.chartCaption', { currency: data.baseCurrency })}
                </p>
              </CardHeader>
              <CardContent>
                <Chart option={chartOption} height={220} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('dash.arAging')}</CardTitle>
                <p className="text-xs text-muted-foreground">{t('dash.arAgingSub')}</p>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {/*
                  BR-DSH-01-07 / DECISION 5 — FIVE bands, and the SAME five the
                  statement draws. This panel listed four (Current / 1–30 /
                  31–60 / 60+) while CRM-04's statement listed five, so a debt
                  75 days late was "Over 60 days" here and "61–90 days" one
                  click away. D5: "one number must not disagree with itself one
                  click away."

                  IT IS A WALK OVER `AGING_BANDS`, NOT FIVE HAND-WRITTEN ROWS.
                  The order, the count and the keys all come from the shared
                  band list, so this panel cannot draw four again, cannot draw
                  them out of order, and cannot invent a sixth — and a band
                  added there appears here without an edit. That is the same
                  property BR-CRM-04-11 buys on the statement ("a band with no
                  money must still render its card"): every band renders, zeros
                  included, because the list is what is iterated rather than
                  the data.

                  THE LABELS ARE THE STATEMENT'S OWN. `partners.stBand_*`, not
                  a `dash.aging*` set — the four dashboard-only keys are
                  retired with the four bands they named. One breakdown means
                  one set of words for it, in both dictionaries.

                  The colour ramp follows NFR-14 and stays aligned with
                  `overdueTone`'s thresholds: green while current, blue and
                  amber through the middle, red at the two oldest bands, since
                  both are what F-14 calls the debt worth chasing.
                */}
                {AGING_BANDS.map((band, _, bands) => {
                  const value = data.arAging[band];
                  const max = Math.max(...bands.map((b) => Number(data.arAging[b])), 1);
                  return (
                    <div key={band}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span>{t(`partners.stBand_${band}`)}</span>
                        <Amount value={value} currency={data.baseCurrency} className="text-sm" />
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', AGING_COLOURS[band])}
                          style={{ width: `${(Number(value) / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              {/**
               * §5 Band 3 row 19 puts **view all** "at the trailing edge of
               * the TITLE ROW", blue — hence this flex row rather than a link
               * appended after the table. The placement is load-bearing:
               * §14.1.12 forbids "a **View all** link at the bottom of any
               * dashboard table" across the customizable shell, and the two
               * statements only agree while this one stays in the header.
               *
               * §6's table says it is "Never hidden", so it is drawn for an
               * empty journal too — the reader with nothing posted yet is the
               * one who most needs to be shown where the ledger is. It sits
               * inside the permission-gated branch, so BR-DSH-01-09's reader
               * is never offered a way into figures they may not read.
               */}
              <div className="flex items-baseline justify-between gap-2">
                <CardTitle className="text-sm">{t('dash.recentTransactions')}</CardTitle>
                <Link
                  to={journalWindow(today)}
                  className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t('dash.viewAll')}
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {/**
               * ERR-DSH-01-02's client half. §14's row asks for `No
               * transactions yet.`; this build says "No POSTED transactions
               * yet." on purpose, and the extra word is the accurate one —
               * BR-DSH-01-10 and §14.11 both hold that no dashboard figure
               * counts a draft. Same shape as BR-PUR-02-12 naming its own
               * example sentence as the defect: the §14 row describes the
               * prototype, and copying it verbatim would be a regression
               * wearing the costume of spec fidelity. The test asserts the
               * bare sentence is ABSENT so a later "restoration" fails.
               */}
              {data.recent.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('dash.noTransactions')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('dash.colDate')}</th>
                      <th className="pb-1 text-start">{t('dash.colDescription')}</th>
                      <th className="pb-1 text-start">{t('dash.colAccount')}</th>
                      <th className="pb-1 text-end">{t('dash.colAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((row, index) => (
                      <tr key={index} className="border-t">
                        <TableCell className="px-0"><Ltr value={row.date} className="font-mono" /></TableCell>
                        <TableCell className="px-0 font-medium">{row.description}</TableCell>
                        <TableCell className="px-0 text-muted-foreground">{row.account}</TableCell>
                        {/**
                         * BR-DSH-01-11 — "An amount in Recent Transactions must
                         * be green when it INCREASES the account and red when it
                         * DECREASES it." The line below is the whole rule, and
                         * the word that matters in it is *the account*: green is
                         * not "money in" and red is not "money out".
                         *
                         * WHICH IS WHY THE JUDGEMENT IS MADE ON THE SERVER AND
                         * NOT HERE. A debit grows an asset or an expense and
                         * shrinks everything else, so the direction cannot be
                         * read off the amount — it needs the account's TYPE,
                         * which is a ledger fact this component never sees.
                         * `DashboardsService.financial()` derives it, and the
                         * browser only paints what it is told; there is no
                         * arrangement of props that could make this cell colour
                         * a rise red.
                         *
                         * A row's amount is printed unsigned (the query takes
                         * whichever of debit/credit is non-zero), so the colour
                         * carries the direction ON ITS OWN. That is the reason
                         * it must not be derived from the number here: there is
                         * no minus sign to derive it from.
                         */}
                        <TableCell className={cn('px-0 text-end', row.direction === 'in' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                          <Amount value={row.amount} />
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
